import React, { useState } from "react";
import { X, Copy, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Format date to DD/MM/YYYY format
 * Handles various date formats and converts them to DD/MM/YYYY
 */
const formatDateToDDMMYYYY = (dateString) => {
  if (!dateString) return dateString;
  
  try {
    // Try to parse the date string
    const date = new Date(dateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      // If parsing fails, try to extract date parts from common formats
      // Handle formats like "12/30/2025" (MM/DD/YYYY) or "30/12/2025" (DD/MM/YYYY)
      const dateMatch = dateString.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
      if (dateMatch) {
        const [, part1, part2, year] = dateMatch;
        // If first part > 12, it's likely DD/MM/YYYY, otherwise assume MM/DD/YYYY
        if (parseInt(part1) > 12) {
          // Already DD/MM/YYYY
          return dateString;
        } else {
          // MM/DD/YYYY - convert to DD/MM/YYYY
          return `${part2.padStart(2, '0')}/${part1.padStart(2, '0')}/${year}`;
        }
      }
      // If no match, return as is
      return dateString;
    }
    
    // Format as DD/MM/YYYY
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    // If all parsing fails, return original string
    return dateString;
  }
};

/**
 * ChatSummaryModal Component - Displays structured chat summary in sections
 */
const ChatSummaryModal = ({ summaryData, onClose }) => {
  const { summary, customer_name, customer_phone, message_count } = summaryData;
  const [copied, setCopied] = useState(false);

  const contactDetails = summary.contact_details || {};
  const sentimentColors = {
    positive: "text-green-600 bg-green-50",
    negative: "text-red-600 bg-red-50",
    neutral: "text-gray-600 bg-gray-50",
  };

  // Format summary as text for copying
  const formatSummaryForCopy = () => {
    let text = `Chat Summary - ${customer_name} (${customer_phone})\n`;
    text += `${message_count} messages analyzed\n\n`;

    // Contact Details
    if (contactDetails.name || contactDetails.phone || contactDetails.email || contactDetails.location || contactDetails.company) {
      text += "CONTACT DETAILS\n";
      if (contactDetails.name) text += `Name: ${contactDetails.name}\n`;
      if (contactDetails.phone) text += `Phone: ${contactDetails.phone}\n`;
      if (contactDetails.email) text += `Email: ${contactDetails.email}\n`;
      if (contactDetails.location) text += `Location: ${contactDetails.location}\n`;
      if (contactDetails.company) text += `Company: ${contactDetails.company}\n`;
      if (contactDetails.other_info) text += `Other: ${contactDetails.other_info}\n`;
      text += "\n";
    }

    // Conversation Summary
    if (summary.conversation_summary) {
      text += `CONVERSATION SUMMARY\n${summary.conversation_summary}\n\n`;
    }

    // Sentiment
    if (summary.sentiment) {
      text += `SENTIMENT: ${summary.sentiment.charAt(0).toUpperCase() + summary.sentiment.slice(1)}\n\n`;
    }

    // Key Points
    if (summary.key_points && summary.key_points.length > 0) {
      text += "KEY POINTS\n";
      summary.key_points.forEach((point, i) => {
        text += `${i + 1}. ${point}\n`;
      });
      text += "\n";
    }

    // Topics
    if (summary.topics_discussed && summary.topics_discussed.length > 0) {
      text += `TOPICS DISCUSSED: ${summary.topics_discussed.join(", ")}\n\n`;
    }

    // Customer Needs
    if (summary.customer_needs && summary.customer_needs.length > 0) {
      text += "CUSTOMER NEEDS\n";
      summary.customer_needs.forEach((need, i) => {
        text += `${i + 1}. ${need}\n`;
      });
      text += "\n";
    }

    // Resolutions
    if (summary.resolutions && summary.resolutions.length > 0) {
      text += "RESOLUTIONS\n";
      summary.resolutions.forEach((resolution, i) => {
        text += `${i + 1}. ${resolution}\n`;
      });
      text += "\n";
    }

    // Next Steps
    if (summary.next_steps && summary.next_steps.length > 0) {
      text += "NEXT STEPS\n";
      summary.next_steps.forEach((step, i) => {
        text += `${i + 1}. ${step}\n`;
      });
      text += "\n";
    }

    // Important Dates
    if (summary.important_dates && summary.important_dates.length > 0) {
      text += "IMPORTANT DATES\n";
      summary.important_dates.forEach((date, i) => {
        const formattedDate = formatDateToDDMMYYYY(date);
        text += `${i + 1}. ${formattedDate}\n`;
      });
    }

    return text.trim();
  };

  const handleCopy = async () => {
    try {
      const text = formatSummaryForCopy();
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="p-3 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Chat Summary</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {customer_name} • {message_count} messages
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopy}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded transition-colors"
                title="Copy summary"
              >
                {copied ? (
                  <>
                    <Check size={14} className="text-green-600" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy size={14} />
                    <span>Copy</span>
                  </>
                )}
              </button>
              <button
                onClick={onClose}
                className="p-1.5 rounded hover:bg-gray-100 transition-colors"
              >
                <X size={16} className="text-gray-600" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Contact Details Section */}
            {(contactDetails.name || contactDetails.email || contactDetails.location || contactDetails.company || contactDetails.other_info) && (
              <section className="pb-3 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Contact Details</h3>
                <div className="space-y-1.5 text-sm text-gray-700">
                  {contactDetails.name && <div><span className="text-gray-500">Name:</span> {contactDetails.name}</div>}
                  {contactDetails.email && <div><span className="text-gray-500">Email:</span> {contactDetails.email}</div>}
                  {contactDetails.location && <div><span className="text-gray-500">Location:</span> {contactDetails.location}</div>}
                  {contactDetails.company && <div><span className="text-gray-500">Company:</span> {contactDetails.company}</div>}
                  {contactDetails.other_info && <div><span className="text-gray-500">Other:</span> {contactDetails.other_info}</div>}
                </div>
              </section>
            )}

            {/* Conversation Summary */}
            {summary.conversation_summary && (
              <section className="pb-3 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Summary</h3>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {summary.conversation_summary}
                </p>
              </section>
            )}

            {/* Sentiment */}
            {summary.sentiment && (
              <div className="pb-3 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Sentiment</h3>
                <span className={`inline-flex items-center px-2.5 py-1 rounded text-xs font-medium ${sentimentColors[summary.sentiment.toLowerCase()] || sentimentColors.neutral}`}>
                  {summary.sentiment.charAt(0).toUpperCase() + summary.sentiment.slice(1)}
                </span>
              </div>
            )}

            {/* Key Points */}
            {summary.key_points && summary.key_points.length > 0 && (
              <section className="pb-3 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Key Points</h3>
                <ul className="space-y-1.5">
                  {summary.key_points.map((point, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-gray-400 mt-0.5">•</span>
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Topics */}
            {summary.topics_discussed && summary.topics_discussed.length > 0 && (
              <section className="pb-3 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Topics</h3>
                <div className="flex flex-wrap gap-1.5">
                  {summary.topics_discussed.map((topic, index) => (
                    <span key={index} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                      {topic}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Customer Needs */}
            {summary.customer_needs && summary.customer_needs.length > 0 && (
              <section className="pb-3 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Customer Needs</h3>
                <ul className="space-y-1.5">
                  {summary.customer_needs.map((need, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-gray-400 mt-0.5">•</span>
                      <span>{need}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Resolutions */}
            {summary.resolutions && summary.resolutions.length > 0 && (
              <section className="pb-3 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Resolutions</h3>
                <ul className="space-y-1.5">
                  {summary.resolutions.map((resolution, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-green-600 mt-0.5">✓</span>
                      <span>{resolution}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Next Steps */}
            {summary.next_steps && summary.next_steps.length > 0 && (
              <section className="pb-3 border-b border-gray-100">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Next Steps</h3>
                <ul className="space-y-1.5">
                  {summary.next_steps.map((step, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start gap-2">
                      <span className="text-gray-400 mt-0.5">→</span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Important Dates */}
            {summary.important_dates && summary.important_dates.length > 0 && (
              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-2">Important Dates</h3>
                <ul className="space-y-1.5">
                  {summary.important_dates.map((date, index) => (
                    <li key={index} className="text-sm text-gray-700">
                      {formatDateToDDMMYYYY(date)}
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100 rounded transition-colors"
            >
              Close
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ChatSummaryModal;

